import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  UserProfile, 
  SecurityScore, 
  AlertItem, 
  ProtectedDocument, 
  FamilyMember, 
  RecoveryCase, 
  SubscriptionPlan 
} from '../types';
import { 
  INITIAL_USER, 
  INITIAL_SECURITY_SCORE, 
  INITIAL_ALERTS, 
  INITIAL_DOCUMENTS, 
  INITIAL_FAMILY, 
  INITIAL_RECOVERY_CASE,
  INITIAL_PLANS 
} from '../data/mockState';
import { resolveAlert as apiResolveAlert } from '../api/alerts';
import { updateRecoveryStepStatus as apiUpdateRecoveryStep } from '../api/recovery';
import { addProtectedDocument as apiAddDoc, removeProtectedDocument as apiRemoveDoc } from '../api/documents';
import { addFamilyMember as apiAddFamily, removeFamilyMember as apiRemoveFamily } from '../api/family';
import { updateSubscription as apiUpdateSub } from '../api/subscriptions';

interface StoreContextType {
  user: UserProfile | null;
  securityScore: SecurityScore;
  alerts: AlertItem[];
  unresolvedAlertsCount: number;
  documents: ProtectedDocument[];
  familyMembers: FamilyMember[];
  recoveryCase: RecoveryCase;
  plans: SubscriptionPlan[];
  currentPlanId: 'free' | 'personal' | 'family';
  isAiModalOpen: boolean;
  openAiModal: () => void;
  closeAiModal: () => void;
  login: (email: string, asAdmin?: boolean) => void;
  logout: () => void;
  resolveAlert: (alertId: string) => Promise<void>;
  toggleRecoveryStep: (stepNumber: number) => Promise<void>;
  addDocument: (doc: Omit<ProtectedDocument, 'id' | 'lastChecked' | 'protectionStatus'>) => Promise<void>;
  removeDocument: (docId: string) => Promise<void>;
  addFamily: (member: { relationship: FamilyMember['relationship']; name: string; email: string; phone?: string }) => Promise<void>;
  removeFamily: (id: string) => Promise<void>;
  upgradePlan: (planId: 'free' | 'personal' | 'family', interval?: 'monthly' | 'annual') => Promise<void>;
  switchUserRole: (role: 'user' | 'admin') => void;
}

const StoreContext = createContext<StoreContextType | null>(null);

export const StoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(() => {
    const saved = localStorage.getItem('idfraud_current_user');
    return saved ? JSON.parse(saved) : INITIAL_USER;
  });

  const [securityScore, setSecurityScore] = useState<SecurityScore>(() => {
    const saved = localStorage.getItem('idfraud_security_score');
    return saved ? JSON.parse(saved) : INITIAL_SECURITY_SCORE;
  });

  const [alerts, setAlerts] = useState<AlertItem[]>(() => {
    const saved = localStorage.getItem('idfraud_alerts_list');
    return saved ? JSON.parse(saved) : INITIAL_ALERTS;
  });

  const [documents, setDocuments] = useState<ProtectedDocument[]>(() => {
    const saved = localStorage.getItem('idfraud_documents_list');
    return saved ? JSON.parse(saved) : INITIAL_DOCUMENTS;
  });

  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>(() => {
    const saved = localStorage.getItem('idfraud_family_members');
    return saved ? JSON.parse(saved) : INITIAL_FAMILY;
  });

  const [recoveryCase, setRecoveryCase] = useState<RecoveryCase>(() => {
    const saved = localStorage.getItem('idfraud_recovery_case');
    return saved ? JSON.parse(saved) : INITIAL_RECOVERY_CASE;
  });

  const [currentPlanId, setCurrentPlanId] = useState<'free' | 'personal' | 'family'>(() => {
    const saved = localStorage.getItem('idfraud_current_plan');
    return (saved as any) || 'personal';
  });

  const [plans] = useState<SubscriptionPlan[]>(INITIAL_PLANS);
  const [isAiModalOpen, setIsAiModalOpen] = useState<boolean>(false);

  const unresolvedAlertsCount = alerts.filter(a => !a.isResolved).length;

  // Recalculate score dynamically whenever alerts or docs change
  const recalculateScore = (alertsList: AlertItem[], docsList: ProtectedDocument[], recCase: RecoveryCase) => {
    const activeBreachAlerts = alertsList.filter(a => !a.isResolved && a.category === 'breach').length;
    const docScore = Math.min(100, 70 + docsList.length * 10);
    const exposureScore = Math.max(40, 100 - activeBreachAlerts * 25);
    const recBonus = Math.round((recCase.completedStepsCount / recCase.totalStepsCount) * 15);

    const emailScore = 90;
    const pwdScore = 78;
    const acctScore = 85;
    const scamScore = 80;
    const creditScore = 92;

    const overall = Math.round((emailScore + pwdScore + acctScore + docScore + scamScore + exposureScore + creditScore) / 7) + recBonus;
    const clampedOverall = Math.min(Math.max(overall, 10), 100);

    const updatedScore: SecurityScore = {
      ...securityScore,
      overallScore: clampedOverall,
      status: clampedOverall >= 80 ? 'good' : clampedOverall >= 60 ? 'attention' : 'risk',
      headline: clampedOverall >= 80 
        ? 'Your identity security is strong, with comprehensive Australian protection active.'
        : clampedOverall >= 60 
          ? 'Your identity security is good, but there are areas that need attention.'
          : 'High identity risk detected. Immediate protective steps are recommended.',
      lastCalculated: new Date().toISOString(),
      categories: {
        ...securityScore.categories,
        idDocuments: {
          ...securityScore.categories.idDocuments,
          score: docScore
        },
        identityExposure: {
          ...securityScore.categories.identityExposure,
          score: exposureScore,
          status: exposureScore >= 80 ? 'good' : 'attention'
        }
      }
    };

    setSecurityScore(updatedScore);
    localStorage.setItem('idfraud_security_score', JSON.stringify(updatedScore));
  };

  const login = (email: string, asAdmin: boolean = false) => {
    const u: UserProfile = {
      ...INITIAL_USER,
      email,
      role: asAdmin ? 'admin' : 'user',
      lastLoginAt: new Date().toISOString()
    };
    setUser(u);
    localStorage.setItem('idfraud_current_user', JSON.stringify(u));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('idfraud_current_user');
  };

  const switchUserRole = (role: 'user' | 'admin') => {
    if (!user) return;
    const updated = { ...user, role };
    setUser(updated);
    localStorage.setItem('idfraud_current_user', JSON.stringify(updated));
  };

  const resolveAlert = async (alertId: string) => {
    const updated = await apiResolveAlert(alertId);
    setAlerts(updated);
    recalculateScore(updated, documents, recoveryCase);
  };

  const toggleRecoveryStep = async (stepNumber: number) => {
    const step = recoveryCase.steps.find(s => s.stepNumber === stepNumber);
    if (!step) return;
    const updated = await apiUpdateRecoveryStep(stepNumber, !step.isCompleted);
    setRecoveryCase(updated);
    recalculateScore(alerts, documents, updated);
  };

  const addDocument = async (doc: Omit<ProtectedDocument, 'id' | 'lastChecked' | 'protectionStatus'>) => {
    const newDoc = await apiAddDoc(doc);
    const updated = [...documents, newDoc];
    setDocuments(updated);
    recalculateScore(alerts, updated, recoveryCase);
  };

  const removeDocument = async (docId: string) => {
    const updated = await apiRemoveDoc(docId);
    setDocuments(updated);
    recalculateScore(alerts, updated, recoveryCase);
  };

  const addFamily = async (member: { relationship: FamilyMember['relationship']; name: string; email: string; phone?: string }) => {
    const newMember = await apiAddFamily(member);
    setFamilyMembers([...familyMembers, newMember]);
  };

  const removeFamily = async (id: string) => {
    const updated = await apiRemoveFamily(id);
    setFamilyMembers(updated);
  };

  const upgradePlan = async (planId: 'free' | 'personal' | 'family', interval: 'monthly' | 'annual' = 'monthly') => {
    await apiUpdateSub(planId, interval);
    setCurrentPlanId(planId);
    localStorage.setItem('idfraud_current_plan', planId);
  };

  const openAiModal = () => setIsAiModalOpen(true);
  const closeAiModal = () => setIsAiModalOpen(false);

  return (
    <StoreContext.Provider
      value={{
        user,
        securityScore,
        alerts,
        unresolvedAlertsCount,
        documents,
        familyMembers,
        recoveryCase,
        plans,
        currentPlanId,
        isAiModalOpen,
        openAiModal,
        closeAiModal,
        login,
        logout,
        resolveAlert,
        toggleRecoveryStep,
        addDocument,
        removeDocument,
        addFamily,
        removeFamily,
        upgradePlan,
        switchUserRole
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export function useStore(): StoreContextType {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
}
